import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { figma } from '../theme/figmaTokens';

const outlinedFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: figma.white,
    backgroundColor: 'rgba(255,255,255,0.08)',
    '& fieldset': { borderColor: figma.border },
    '&:hover fieldset': { borderColor: figma.textMutedLight },
    '&.Mui-focused fieldset': { borderColor: figma.cyan, borderWidth: 2 },
  },
  '& .MuiOutlinedInput-input::placeholder': {
    color: figma.white,
    opacity: 1,
  },
};

const inputLabelProps = {
  shrink: true,
  sx: {
    color: figma.white,
    '&.Mui-focused': { color: figma.white },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' },
  },
};

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
    setResetCode(digits);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (resetCode.length !== 4) {
      setError('Please enter the 4-digit code sent to your email.');
      return;
    }
    if (!newPassword) {
      setError('Please enter a new password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    setLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 600));
      navigate('/login', { replace: true, state: { resetMessage: 'Your password has been updated. You can sign in.' } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: figma.pageBg }}>
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              width: '100%',
              maxWidth: 440,
              backgroundColor: figma.cardBg,
              border: `1px solid ${figma.border}`,
              borderRadius: 1,
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/Quu_Logo_Gray.png`}
                alt="Quu"
                sx={{ height: 44, width: 'auto', mb: 2, mx: 'auto', display: 'block' }}
              />
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  color: figma.cyan,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  mb: 1,
                }}
              >
                HotQuus
              </Typography>
              <Typography variant="body1" sx={{ color: figma.textMuted, fontWeight: 500 }}>
                Reset your password
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading}
                autoComplete="new-password"
                InputLabelProps={inputLabelProps}
                sx={outlinedFieldSx}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading}
                autoComplete="new-password"
                InputLabelProps={inputLabelProps}
                sx={outlinedFieldSx}
              />
              <TextField
                fullWidth
                label="Enter the 4 digit reset code sent to your email:"
                value={resetCode}
                onChange={handleCodeChange}
                margin="normal"
                required
                disabled={loading}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 4,
                  'aria-label': 'Four digit reset code',
                }}
                InputLabelProps={{
                  ...inputLabelProps,
                  sx: {
                    ...inputLabelProps.sx,
                    whiteSpace: 'normal',
                    lineHeight: 1.3,
                    fontSize: '0.8rem',
                  },
                }}
                sx={outlinedFieldSx}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 1.5,
                  py: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  backgroundColor: figma.cyan,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#2d8fc4',
                    boxShadow: 'none',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Update Password'}
              </Button>

              <Button
                type="button"
                fullWidth
                variant="outlined"
                size="large"
                disabled={loading}
                onClick={() => navigate('/login')}
                sx={{
                  py: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: figma.cyan,
                  borderColor: figma.cyan,
                  '&:hover': {
                    borderColor: figma.cyan,
                    backgroundColor: 'rgba(55, 162, 217, 0.08)',
                  },
                }}
              >
                Back to Login
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
